// modules/reports/InKindBaseReport.js
import * as React from "react"
import { useState, useEffect } from "react";
import {
  Title,
  useDataProvider,
  useNotify,
  Button,
  usePermissions,
} from "react-admin";
import { Link } from 'react-router-dom';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  MenuItem,
  Select,
  Stack
} from "@mui/material";
import {
  PieChart,
  ChartsTooltip,
  PieArcLabel,
} from "@mui/x-charts";
import {
  VolunteerActivism as DonateIcon,
  RequestQuote as NeedIcon,
  Handshake as MatchIcon,
  LocalOffer as OfferIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { TextField as MuiTextField } from "@mui/material";
import { GenericTable } from "../components/inkind/generic-table";

const token = JSON.parse(localStorage.getItem("token"));

// Modern Summary Card Component
export const SummaryCard = ({ title, value, icon: Icon, secondaryValue, color = "primary" }) => {
  const theme = useTheme();

  const colorMap = {
    primary: theme.palette.primary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  };

  return (
    <Card
      sx={{
        height: "100%",
        boxShadow: "0 4px 20px 0 rgba(0,0,0,0.12)",
        borderLeft: `4px solid ${colorMap[color]}`,
        transition: "all 0.3s ease",
        background: `linear-gradient(135deg, ${alpha(
          colorMap[color],
          0.1
        )} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
        backdropFilter: "blur(8px)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 25px 0 rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "24px !important",
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${colorMap[color]} 0%, ${alpha(colorMap[color], 0.8)} 100%)`,
            borderRadius: "12px",
            width: 60,
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
            color: theme.palette.primary.contrastText,
            boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
          }}
        >
          <Icon fontSize="medium" />
        </Box>
        <Box>
          <Typography
            variant="subtitle2"
            color="textSecondary"
            gutterBottom
            sx={{ opacity: 0.8 }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            component="div"
            fontWeight="800"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.text.primary
                } 0%, ${alpha(theme.palette.text.primary, 0.8)} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {value}
          </Typography>
          {secondaryValue && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ opacity: 0.7 }}
            >
              {secondaryValue}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Main InKindBaseReport Component
const InKindBaseReport = ({
  title = "In-Kind Contributions Dashboard",
  description = "Match donations with needs and track in-kind contributions",
  type = "offers", // "offers" or "services"
  categoryEndpoint = "sub_category", // or "servicetypes"
  categoryLabel = "Category",
  resourceName = "admin/in-kind-offers",
  statisticsEndpoint = "admin/in-kind-offers/statistics"
}) => {
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const theme = useTheme();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(0); // MUI is 0-based
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const token = JSON.parse(localStorage.getItem("token"));

  const isService = type === "services";

  // Function to check if user is manager
  const isManager = permissions === 'manager';

  // Function to check if user can see action buttons
  const canSeeActionButtons = !isManager && (permissions === 'admin' || permissions === 'operater');

  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        // Fetch states
        const stateResponse = await fetch(
          `${process.env.REACT_APP_URL}/admin/state`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        const statesData = await stateResponse.json();
        setStates(statesData);

        // Fetch categories based on type
        const categoriesResponse = await fetch(
          `${process.env.REACT_APP_URL}/admin/${categoryEndpoint}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: "Bearer " + token,
            },
          }
        );
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData["data"] || categoriesData);
      } catch (err) {
        console.error("Error fetching metadata:", err);
      }
    };
    fetchMetaData();
  }, [type, categoryEndpoint, token]);

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);

        // Build filter parameters
        const params = new URLSearchParams();
        const filters = [];

        if (selectedState) {
          filters.push(`state||$eq||${selectedState}`);
        }
        if (selectedCategory) {
          const categoryField = isService ? 'service_id' : 'subCategory_id';
          filters.push(`${categoryField}||$eq||${selectedCategory}`);
        }
        if (startDate) {
          filters.push(`created_at_gte||$cont||${startDate}`);
        }
        if (endDate) {
          filters.push(`created_at_lte||$cont||${endDate}`);
        }

        // Always filter for in-kind items
        // params.append('page', '1');
        // params.append('limit', '50');

        params.append('page', page + 1);
        params.append('limit', rowsPerPage);

        if (filters.length > 0) {
          params.append('filter', JSON.stringify(filters));
        }

        // Fetch in-kind data
        const url = `${process.env.REACT_APP_URL}/${resourceName}?${params.toString()}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setReportData(data);

        // Also fetch statistics
        const statsResponse = await fetch(
          `${process.env.REACT_APP_URL}/${statisticsEndpoint}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: "Bearer " + token,
            },
          }
        );

        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setReportData(prev => ({ ...prev, statistics: statsData }));
        }

      } catch (err) {
        console.error("Error fetching in-kind report data:", err);
        setError(err.message || "Failed to fetch data");
        notify("Error loading in-kind report data", { type: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [selectedState, selectedCategory, startDate, endDate, token, notify, resourceName, statisticsEndpoint, isService, page, rowsPerPage]);

  const handleMatch = async (donationId, needId, notes) => {
    try {
      // Update donation status to donated
      await dataProvider.update(resourceName, {
        id: donationId,
        data: { status: 'donated' },
      });

      // Update need status to sold
      await dataProvider.update(resourceName, {
        id: needId,
        data: { status: 'sold' },
      });

      notify('Successfully matched donation with need!', { type: 'success' });

      // Refresh data
      setReportData(null);
      setLoading(true);

      // Re-fetch data
      const fetchReportData = async () => {
        const params = new URLSearchParams();
        params.append('page', '1');
        params.append('limit', '50');

        const url = `${process.env.REACT_APP_URL}/${resourceName}?${params.toString()}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setReportData(data);
        }
        setLoading(false);
      };

      fetchReportData();
    } catch (error) {
      console.error("Match error:", error);
      notify('Error matching donation with need', { type: 'error' });
    }
  };

  const handleAssignDelivery = () => {
    // Refresh the data after successful assignment
    refreshReportData();
    notify('Delivery company assigned successfully!', { type: 'success' });
  };

  const markAsDonated = async (id) => {
    const endpoint = isService
      ? `/admin/in-kind-services/${id}/donated`
      : `/admin/in-kind-offers/${id}/donated`;

    const response = await fetch(
      `${process.env.REACT_APP_URL}${endpoint}`,
      {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      }
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to mark as donated');
    }

    return response.json();
  };

  const refreshReportData = async () => {
    setReportData(null);
    setLoading(true);

    try {
      const params = new URLSearchParams();
      const filters = [];

      if (selectedState) {
        filters.push(`state||$eq||${selectedState}`);
      }
      if (selectedCategory) {
        const categoryField = isService ? 'service_id' : 'subCategory_id';
        filters.push(`${categoryField}||$eq||${selectedCategory}`);
      }
      if (startDate) {
        filters.push(`created_at_gte||$cont||${startDate}`);
      }
      if (endDate) {
        filters.push(`created_at_lte||$cont||${endDate}`);
      }

      // params.append('page', '1');
      // params.append('limit', '50');

      params.append('page', page + 1);
      params.append('limit', rowsPerPage);

      if (filters.length > 0) {
        params.append('filter', JSON.stringify(filters));
      }

      const url = `${process.env.REACT_APP_URL}/${resourceName}?${params.toString()}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setReportData(data);

      // Also fetch statistics
      const statsResponse = await fetch(
        `${process.env.REACT_APP_URL}/${statisticsEndpoint}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setReportData(prev => ({ ...prev, statistics: statsData }));
      }

    } catch (err) {
      console.error("Error refreshing report data:", err);
      setError(err.message || "Failed to refresh data");
      notify("Error refreshing in-kind report data", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsDonated = async (offerId) => {
    try {
      await markAsDonated(offerId);
      notify('Successfully marked as donated!', { type: 'success' });
      await refreshReportData();
    } catch (error) {
      console.error('Error marking as donated:', error);
      notify('Failed to mark as donated', { type: 'error' });
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 3 }}>
        Failed to load in-kind report data. Please try again later.
        <Box mt={1} fontFamily="monospace">
          {error}
        </Box>
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="warning" sx={{ my: 3 }}>
        No in-kind report data available.
      </Alert>
    );
  }

  // Calculate stats from data
  const items = reportData.data || [];
  const donations = items.filter(item => {
    const typeField = isService ? item.service_type : item.offer_type;
    return typeField === 'donate';
  });
  const needs = items.filter(item => {
    const typeField = isService ? item.service_type : item.offer_type;
    return typeField === 'need';
  });
  const activeDonations = donations.filter(d => d.status === 'active');
  const activeNeeds = needs.filter(n => n.status === 'active');

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.default,
          0.9
        )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        p: 3,
        borderRadius: "16px",
      }}
    >
      <Title title={`${title} Reports`} />

      <Box sx={{ mb: 4 }}>


        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
          <Box>
            <Typography
              variant="h4"
              fontWeight="800"
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: "inline-block",
              }}
            >
              {title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {description}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2}>
            {/* Create In-Kind Offer Button */}
            {canSeeActionButtons && (
              <Button
                component={Link}
                to="/in-kind-offers/create"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  }
                }}
              >
                Create In-Kind {type === 'services' ? 'Service' : 'Offer'}
              </Button>
            )}

            {/* Quick Actions Menu (Optional) */}
            {/*
        <Button
          variant="outlined"
          color="primary"
          startIcon={<DonateIcon />}
          onClick={() => {
            // Quick action for creating donation
            notify('Redirecting to create donation...', { type: 'info' });
            // You could add a quick modal or redirect with pre-filled values
          }}
        >
          Quick Donation
        </Button>
        */}
          </Stack>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Donations"
            value={donations.length}
            icon={DonateIcon}
            secondaryValue={`${activeDonations.length} active`}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Needs"
            value={needs.length}
            icon={NeedIcon}
            secondaryValue={`${activeNeeds.length} active`}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Completed Matches"
            value={Math.floor((donations.filter(d => d.status === 'donated').length +
              needs.filter(n => n.status === 'donated').length))}
            icon={MatchIcon}
            secondaryValue="Successfully matched"
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Ready to Match"
            value={activeDonations.length + activeNeeds.length}
            icon={OfferIcon}
            secondaryValue={`${activeDonations.length} donations + ${activeNeeds.length} needs`}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontWeight: "600",
                textTransform: "none",
                fontSize: "0.875rem",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label={`All In-Kind ${type === 'services' ? 'Services' : 'Offers'}`} />
            <Tab label="Active Donations" />
            <Tab label="Active Needs" />
          </Tabs>
        </Paper>
      </Box>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2">State</Typography>
              <Select
                fullWidth
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2">{categoryLabel}</Typography>
              <Select
                fullWidth
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2">From</Typography>
              <DatePicker
                value={startDate || null}
                onChange={(newDate) => {
                  const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
                  setStartDate(iso);
                }}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    fullWidth
                    size="small"
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2">To</Typography>
              <DatePicker
                value={endDate || null}
                onChange={(newDate) => {
                  const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
                  setEndDate(iso);
                }}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    fullWidth
                    size="small"
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Statistics Charts */}
          <Grid container spacing={3}>
            {reportData.typeDistribution && reportData.typeDistribution.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: "blur(8px)",
                    height: "100%",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {categoryLabel} Distribution
                    </Typography>
                    <Box sx={{ height: 300 }}>
                      <PieChart
                        series={[
                          {
                            data: reportData.typeDistribution.map((item, index) => ({
                              id: item.name || `category-${index}`,
                              label: item.name || `${categoryLabel} ${index + 1}`,
                              value: parseInt(item.count || item.value || 0),
                            })),
                            innerRadius: 40,
                            outerRadius: 100,
                            paddingAngle: 2,
                            cornerRadius: 6,
                            highlightScope: { faded: "global", highlighted: "item" },
                            faded: {
                              innerRadius: 30,
                              additionalRadius: -30,
                              color: "gray",
                            },
                            arcLabel: (item) => `${item.value}`,
                            arcLabelMinAngle: 20,
                          },
                        ]}
                        slotProps={{
                          legend: {
                            direction: "row",
                            position: { vertical: "bottom", horizontal: "middle" },
                            padding: 0,
                            labelStyle: {
                              fontSize: 12,
                              fill: theme.palette.text.primary,
                            },
                          },
                        }}
                      >
                        <ChartsTooltip />
                        <PieArcLabel />
                      </PieChart>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {reportData.userDemographics?.gender && reportData.userDemographics.gender.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: "blur(8px)",
                    height: "100%",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Gender Distribution
                    </Typography>
                    <Box sx={{ height: 300 }}>
                      <PieChart
                        series={[
                          {
                            data: reportData.userDemographics.gender.map((item) => ({
                              id: item.gender,
                              label: item.gender === 'male' ? 'Male' : 'Female',
                              value: parseInt(item.count || item.value || 0),
                            })),
                            innerRadius: 40,
                            outerRadius: 100,
                            paddingAngle: 2,
                            cornerRadius: 6,
                            highlightScope: { faded: "global", highlighted: "item" },
                            faded: {
                              innerRadius: 30,
                              additionalRadius: -30,
                              color: "gray",
                            },
                            arcLabel: (item) => `${item.value}`,
                            arcLabelMinAngle: 20,
                          },
                        ]}
                        slotProps={{
                          legend: {
                            direction: "row",
                            position: { vertical: "bottom", horizontal: "middle" },
                            padding: 0,
                            labelStyle: {
                              fontSize: 12,
                              fill: theme.palette.text.primary,
                            },
                          },
                        }}
                      >
                        <ChartsTooltip />
                        <PieArcLabel />
                      </PieChart>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </>
      )}

      {/* All In-Kind Items Tab */}
      {activeTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            All In-Kind {type === 'services' ? 'Services' : 'Offers'} ({items.length})
          </Typography>

          <GenericTable
            data={items}
            title={`in-kind ${type === 'services' ? 'services' : 'offers'}`}
            onMarkAsDonated={handleMarkAsDonated}
            onAssignDelivery={handleAssignDelivery}
            isService={isService}
            resourceName={resourceName}
            // showAssignButton={true}
            showAssignButton={canSeeActionButtons}
            canSeeActionButtons={canSeeActionButtons}
            page={page}
            rowsPerPage={rowsPerPage}
            total={reportData.total}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        </Box>
      )}

      {/* Active Donations Tab */}
      {activeTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Active Donations ({activeDonations.length})
          </Typography>

          <GenericTable
            data={activeDonations}
            title="active donations"
            onMarkAsDonated={handleMarkAsDonated}
            isService={isService}
            resourceName={resourceName}
            // showAssignButton={true}
            showAssignButton={canSeeActionButtons}
            canSeeActionButtons={canSeeActionButtons}
            page={page}
            rowsPerPage={rowsPerPage}
            total={reportData.total}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        </Box>
      )}

      {/* Active Needs Tab */}
      {activeTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Active Needs ({activeNeeds.length})
          </Typography>

          <GenericTable
            data={activeNeeds}
            title="active needs"
            onMarkAsDonated={handleMarkAsDonated}
            isService={isService}
            resourceName={resourceName}
            // showAssignButton={true}
            showAssignButton={canSeeActionButtons}
            canSeeActionButtons={canSeeActionButtons}
            page={page}
            rowsPerPage={rowsPerPage}
            total={reportData.total}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        </Box>
      )}
    </Box>
  );
};

export default InKindBaseReport;